export const isShipmentFullyPaid = (shipment) =>
  Number(shipment?.balance_amount ?? 0) <= 0;

export const isCreditClientShipment = (shipment) => {
  const clientId = shipment?.shipment_client_id;
  if (clientId != null && clientId !== '' && Number(clientId) > 0) return true;
  const mode = String(shipment?.mode_of_payment || shipment?.payment_mode || '').toLowerCase();
  return mode === 'credit';
};

/** Cash or UPI still due from the receiver. Credit-client freight stays on the client. */
export const requiresDeliveryCollection = (shipment) =>
  !isCreditClientShipment(shipment) && !isShipmentFullyPaid(shipment);

export const QR_TERMINAL_STATUSES = ['delivered', 'rejected', 'returned'];

export const getQrTransitionEligibility = (shipment, targetStatus) => {
  const current = String(shipment?.status || '').toLowerCase();
  const target = String(targetStatus || '').toLowerCase();

  if (QR_TERMINAL_STATUSES.includes(current)) {
    return {
      eligible: false,
      skip: true,
      reason: `Cannot update ${shipment.status} shipment`,
    };
  }

  if (target === 'dispatched') {
    if (current === 'booked' && shipment?.can_dispatch) {
      return { eligible: true };
    }
    if (current !== 'booked') {
      return {
        eligible: false,
        skip: true,
        reason: 'Dispatch is only for booked shipments',
      };
    }
    return {
      eligible: false,
      skip: true,
      reason: 'You can dispatch only your own shipment',
    };
  }

  if (target === 'received') {
    if (current === 'dispatched' || current === 'received') {
      return {
        eligible: true,
        reapply: current === 'received',
      };
    }
    return {
      eligible: false,
      skip: true,
      reason: `Cannot change from ${shipment.status} to Received`,
    };
  }

  if (target === 'delivered') {
    if (current !== 'received') {
      return {
        eligible: false,
        skip: true,
        reason: `Must be Received first (current: ${shipment.status})`,
      };
    }
    if (requiresDeliveryCollection(shipment)) {
      return { eligible: false, skip: true, reason: 'Payment not complete' };
    }
    return { eligible: true };
  }

  return { eligible: false, skip: true, reason: 'Invalid status' };
};

export const getEligibleShipmentsForQrStatus = (shipments, targetStatus) => {
  const eligible = [];
  const skipped = [];

  for (const shipment of shipments || []) {
    const result = getQrTransitionEligibility(shipment, targetStatus);
    if (result.eligible) {
      eligible.push({ ...shipment, reapply: result.reapply });
    } else {
      skipped.push({ ...shipment, skipReason: result.reason });
    }
  }

  return { eligible, skipped };
};

export const getActiveQrShipments = (shipments) =>
  (shipments || []).filter(
    (s) => !QR_TERMINAL_STATUSES.includes(String(s.status || '').toLowerCase()),
  );

export const normalizeQrResolveResponse = (response) => {
  const data = response?.data || response;
  const shipments = Array.isArray(data?.shipments) && data.shipments.length
    ? data.shipments
    : data?.shipment_id
      ? [data]
      : [];

  return {
    shipments,
    failed: data?.failed || [],
    count: shipments.length,
    scanMethod: data?.scan_method || null,
  };
};

export const getQrStatusOptions = (resolved) => {
  if (!resolved) {
    return { options: [], autoSelect: null, paymentBlocked: false };
  }

  const current = String(resolved.status || '').toLowerCase();

  if (QR_TERMINAL_STATUSES.includes(current)) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: `${resolved.status} shipments cannot be updated.`,
    };
  }

  if (current === 'booked') {
    if (resolved.can_dispatch) {
      return { options: ['Dispatched'], autoSelect: 'Dispatched', paymentBlocked: false };
    }
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'You can dispatch only your own booked shipment.',
    };
  }

  if (current === 'dispatched') {
    return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
  }

  if (current === 'received') {
    return {
      options: ['Received', 'Delivered'],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: requiresDeliveryCollection(resolved)
        ? 'Received can be confirmed again. Delivered requires full payment.'
        : isCreditClientShipment(resolved)
          ? 'This is a credit client. Deliver without collecting amount or payment mode.'
          : 'Received can be confirmed again, or mark as Delivered.',
    };
  }

  return { options: [], autoSelect: null, paymentBlocked: false };
};

export const getBulkStatusOptions = (selectedIds, shipments) => {
  const selected = shipments.filter((s) => selectedIds.has(s.shipment_id));
  const active = getActiveQrShipments(selected);

  if (!active.length) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'All scanned shipments are already Delivered or cannot be updated.',
    };
  }

  const hasBookedDispatch = active.some(
    (s) => String(s.status).toLowerCase() === 'booked' && s.can_dispatch,
  );
  const hasDispatched = active.some(
    (s) => String(s.status).toLowerCase() === 'dispatched',
  );
  const hasReceived = active.some(
    (s) => String(s.status).toLowerCase() === 'received',
  );
  const hasUnpaid = active.some((s) => requiresDeliveryCollection(s));
  const hasCredit = active.some((s) => isCreditClientShipment(s));

  if (hasBookedDispatch && !hasDispatched && !hasReceived) {
    return { options: ['Dispatched'], autoSelect: 'Dispatched', paymentBlocked: false };
  }

  if (!hasReceived && hasDispatched && !hasBookedDispatch) {
    return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
  }

  if (hasBookedDispatch && (hasDispatched || hasReceived)) {
    const options = ['Dispatched'];
    if (hasDispatched || hasReceived) options.push('Received');
    if (hasReceived) options.push('Delivered');
    return {
      options,
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'Dispatched is only for your booked shipments. Received is for shipments already on a dispatch.',
    };
  }

  if (hasReceived) {
    return {
      options: ['Received', 'Delivered'],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: hasUnpaid
        ? 'Received can be applied again. Delivered only for fully paid Received shipments.'
        : hasCredit
          ? 'This is a credit client. Deliver without collecting amount or payment mode.'
          : 'Received can be confirmed again, or mark Received shipments as Delivered.',
    };
  }

  if (!hasDispatched && !hasReceived) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'You can dispatch only your own booked shipment.',
    };
  }

  return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
};

export const getQrBatchStatusOptions = (shipments) => {
  if (!shipments?.length) {
    return { options: [], autoSelect: null, paymentBlocked: false };
  }
  if (shipments.length === 1) {
    return getQrStatusOptions(shipments[0]);
  }
  const selectedIds = new Set(shipments.map((s) => s.shipment_id));
  return getBulkStatusOptions(selectedIds, shipments);
};
